<template>
  <nx-taxonomy-selector
    :label="label"
    :type="type"
    :data-vv-name="name"
    :data-vv-scope="scope"
    :data-vv-as="label"
    :value="value"
    :auth="auth"
    :disabled="disabled"
    v-bind="$attrs"
    v-validate:value="validation"
    data-vv-validate-on="input|blur"
    :error-messages="errorMessages"
    :required="required"
    :additional-filters="computedAdditionalFilters"
    @authorized="$emit('authorized', $event)"
    @input="onInput"
    @blur="onBlur"
    @focus="onFocus"
  ></nx-taxonomy-selector>
</template>

<script>
import { isObject, isArray, isEmpty } from 'lodash';
import FormElement from '@/modules/form/mixins/form-element';
import Selector from './Selector';
import { toArray } from '@/utils';

/**
 * Taxonomy Selector.
 *
 * @property {number} [limit] Maximun number of taxonomies that can be selected.
 * @property {(TaxonomyCard|TaxonomyCard[])} [value] The initial selected taxonomies.
 * @property {[]} [type] Taxonomy types allowed in the module.
 */
export default {
  name: 'nx-taxonomy-form-selector',
  mixins: [FormElement],
  components: {
    [Selector.name]: Selector,
  },
  props: {
    type: {
      type: [String, Array],
    },
    value: {
      type: [Array, Object, Number],
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
    additionalFilters: {
      type: Object,
    },
    excludedIds: {
      type: Array,
    },
    excludedIdsFromField: {
      type: String,
    },
    auth: {
      type: Object,
      default() {
        return {
          actions: {
            ADD: null, // Specify bbnx operation when adding taxonomies for validation
            DELETE: null, // Specify bbnx operation when removing current taxonomies
          },
          params: {}, // Object of params that will be sent to bbnx to validate action
        };
      },
    },
    emitObject: Boolean,
  },
  computed: {
    computedExcludedIds() {
      if (!isEmpty(this.excludedIds)) {
        return this.excludedIds;
      }
      if (!this.excludedIdsFromField) {
        return null;
      }
      const fromFieldValue = this.formValue[this.excludedIdsFromField];
      if (!fromFieldValue
        || (isArray(fromFieldValue) && fromFieldValue.length === 0)) {
        return null;
      }
      return toArray(fromFieldValue).map(val => (isObject(val) ? val.id : val));
    },
    computedAdditionalFilters() {
      const filters = {
        excludedIds: this.computedExcludedIds,
      };
      if (this.additionalFilters) {
        return { ...filters, ...this.additionalFilters };
      }
      return filters;
    },
  },
  methods: {
    normalizeSelectorValue(value) {
      if (this.emitObject) {
        return value || null;
      }
      if (Array.isArray(value)) {
        return value.filter(item => !!item).map(item => item.id);
      }
      return value ? value.id : null;
    },
    onInput(inputValue) {
      const value = this.normalizeSelectorValue(inputValue);
      this.$parentForm.updateField(this.name, value, inputValue);
      this.$emit('input', value);
    },
    /**
     * Default value handler.
     * Implemented as a method so it can be overrided.
     */
    onDefault(defaultValue) {
      this.$nextTick(() => this.onInput(defaultValue));
    },
    onBlur(blurValue) {
      this.flags.focused = false;
      this.$emit('blur', this.normalizeSelectorValue(blurValue));
    },
  },
};
</script>
